import * as React from 'react'
import { createPortal } from 'react-dom'
import { DatePickerPopup } from '@/modules/settings/pages/exchange-rate/DatePickerSingle'
import { faCalendar, FaIcon, faXmark } from '@/lib/fa'

type DatePickerWrapperProps = {
  placeholder?: string
  iconAlign?: 'left' | 'right'
  size?: 'small' | 'large'
  clearable?: boolean
  confirmSelection?: boolean
  value: string | null
  setValue: (next: string | null) => void
}

export const DatePickerWrapper: React.FC<DatePickerWrapperProps> = ({
  placeholder = 'RRRR-MM-DD',
  iconAlign = 'left',
  size = 'large',
  clearable,
  confirmSelection = true,
  value,
  setValue,
}) => {
  const [open, setOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })

  const handleConfirm = (fullDate: string) => {
    setValue(fullDate)
    setOpen(false)
  }

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !open
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      })
    }
    setOpen(willOpen)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setValue(null)
    setOpen(false)
  }

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="tw-relative tw-z-50 tw-inline-block">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        type="button"
        className={`tw-flex tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-border tw-px-2 tw-py-1 tw-text-base-content hover:tw-bg-[#F5F5F5] ${
          open
            ? 'tw-border-2 tw-border-primary'
            : 'tw-border-neutral/20 tw-bg-transparent'
        } ${iconAlign === 'right' ? 'tw-flex-row-reverse' : 'tw-flex-row'} ${
          size === 'small' ? 'tw-py-1' : 'tw-py-2'
        }`}
      >
        <FaIcon
          icon={faCalendar}
          className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
        />

        <div className="tw-flex tw-items-center tw-gap-2">
          <p className="tw-text-sm">{value || placeholder}</p>

          {clearable && value && (
            <span onClick={handleDeleteClick} className="tw-inline-flex">
              <FaIcon
                icon={faXmark}
                className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
              />
            </span>
          )}
        </div>
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tw-fixed tw-z-[99999] tw-shadow-md"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
          >
            <DatePickerPopup
              value={value ?? null}
              submit={handleConfirm}
              confirmSelection={confirmSelection}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}
